import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { View } from 'react-native';
import { Props } from 'shared/@types';
import { H1, H2 } from 'shared/components/title';
import { Image } from 'shared/components/image';
import { Button } from 'shared/components/button';

import { PaymentBrands } from 'shared/components/payment-brands';
import { useTranslation } from 'react-i18next';
import catPeace from 'shared/assets/doodles/cat-peace.png';
import bankidNo from 'shared/assets/brands/bankid-no.png';
import bankidSe from 'shared/assets/brands/bankid-se.png';
import mitid from 'shared/assets/brands/mitid.png';
import ftn from 'shared/assets/brands/ftn.png';
import handelsbanken from 'shared/assets/brands/handelsbanken.png';
import stripe from 'shared/assets/brands/stripe.png';
import { Text } from 'shared/components/text';
import { PurchaseAgreement } from 'shared/components/illustrations/purchase-agreement';
import { Reservation } from 'shared/components/illustrations/reservation';
import { Payment } from 'shared/components/illustrations/payment';
import { Pickup } from 'shared/components/illustrations/pickup';
import { useRoute } from 'shared/library/routes/route';
import { InfoGrid } from 'shared/components/info-grid';
import { ReviewsSummaryCard } from 'shared/components/reviews-summary-card';
import { ReviewsSegment } from 'shared/components/reviews-segment';
import { usePetpayBuyerFeatures } from 'shared/library/use-petpay-buyer-features';
import { withNativeGradientFallback } from 'shared/library/native-gradient';

const PetPayForBuyersScreen = ({}: Props) => {
  const { t } = useTranslation('screens');
  const route = useRoute();
  const petpayFeatures = usePetpayBuyerFeatures({ includeComingSoon: false });

  return (
    <View>
      <View
        className={withNativeGradientFallback(
          'from-brand-cream to-brand-cream-light items-center bg-gradient-to-b p-6 md:py-12',
        )}
      >
        <Image
          src={catPeace}
          alt={'Cat Doodle'}
          containerClassName={'-m-1.5 size-16 md:size-24'}
          imageClassName={'object-contain'}
        />

        <H1
          title={t('petpay.for_buyers.title')}
          subtitle={t('petpay.for_buyers.description')}
          center={true}
        />

        <View className={'items-center justify-center gap-3 md:flex-row md:gap-1.5'}>
          <PaymentBrands />
          <ReviewsSummaryCard compact={true} />
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-12'}>
        <View className={`mx-auto w-full max-w-screen-xl`}>
          <H2
            title={t('petpay.for_buyers.how_it_works')}
            subtitle={t('petpay.for_buyers.how_it_works_description')}
          />

          <View className={'gap-6 md:flex-row md:gap-12'}>
            <View className={'flex-row gap-3 md:flex-1 md:flex-col'}>
              <View className={'w-2/5 md:w-full'}>
                <Reservation />
              </View>
              <View className={'flex-1'}>
                <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                  1. {t('petpay.for_buyers.reservation')}
                </Text>
                <Text className={'text-brand-brown-dark/70'}>
                  {t('petpay.for_buyers.reservation_description')}
                </Text>
              </View>
            </View>

            <View className={'flex-row gap-3 md:flex-1 md:flex-col'}>
              <View className={'w-2/5 md:w-full'}>
                <PurchaseAgreement />
              </View>
              <View className={'flex-1'}>
                <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                  2. {t('petpay.for_buyers.purchase_agreement')}
                </Text>
                <Text className={'text-brand-brown-dark/70'}>
                  {t('petpay.for_buyers.purchase_agreement_description')}
                </Text>
              </View>
            </View>

            <View className={'flex-row gap-3 md:flex-1 md:flex-col'}>
              <View className={'w-2/5 md:w-full'}>
                <Payment />
              </View>
              <View className={'flex-1'}>
                <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                  3. {t('petpay.for_buyers.payment')}
                </Text>
                <Text className={'text-brand-brown-dark/70'}>
                  {t('petpay.for_buyers.payment_description')}
                </Text>
              </View>
            </View>

            <View className={'flex-row gap-3 md:flex-1 md:flex-col'}>
              <View className={'w-2/5 md:w-full'}>
                <Pickup />
              </View>
              <View className={'flex-1'}>
                <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                  4. {t('petpay.for_buyers.pickup')}
                </Text>
                <Text className={'text-brand-brown-dark/70'}>
                  {t('petpay.for_buyers.pickup_description')}
                </Text>
              </View>
            </View>
          </View>

          <View className={'mt-6 md:my-12'} />

          <H2
            title={t('petpay.for_buyers.whats_included')}
            subtitle={t('petpay.for_buyers.whats_included_description')}
          />

          <View className={'mt-3'}>
            <InfoGrid large={true} items={petpayFeatures} />
          </View>

          <Text
            className={
              'text-brand-brown-dark/70 mt-6 text-xs leading-4 md:mt-8 md:text-sm md:leading-5'
            }
          >
            {t('petpay.for_buyers.tractive_disclaimer')}
          </Text>

          <View className={'mt-6 md:my-12'} />

          <View
            className={
              'flex-row flex-wrap items-center justify-center gap-3 md:flex-nowrap md:gap-3 lg:gap-6'
            }
          >
            <Image
              src={bankidSe}
              alt={'BankID SE Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain'}
            />
            <Image
              src={mitid}
              alt={'MitID Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain p-1 md:p-2'}
            />
            <Image
              src={ftn}
              alt={'FTN Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain p-2.5 md:p-5'}
            />
            <Image
              src={bankidNo}
              alt={'BankID NO Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain'}
            />
            <Image
              src={stripe}
              alt={'Stripe Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain'}
            />
            <Image
              src={handelsbanken}
              alt={'Handelsbanken Logo'}
              containerClassName={'h-10 w-1/4 md:h-16 md:w-20 lg:w-28'}
              imageClassName={'object-contain'}
            />
          </View>

          <Text className={'text-brand-brown-dark mt-6 text-center'}>
            {t('petpay.for_buyers.cta_text')}
          </Text>

          <View className={'mt-6 flex-row flex-wrap justify-center gap-3'}>
            <Button
              text={t('web:layout.footer.sections.dogs.buying_a_dog')}
              icon={falAngleRight}
              color={'brown'}
              href={route('buying-a-dog')}
            />
            <Button
              text={t('web:layout.footer.sections.cats.buying_a_cat')}
              icon={falAngleRight}
              color={'brown'}
              href={route('buying-a-cat')}
            />
          </View>

          <View className={'mt-6 md:my-12'} />

          <ReviewsSegment role={'buyer'} />
        </View>
      </View>
    </View>
  );
};

export { PetPayForBuyersScreen };
